// Return to the page a signed-out reader was on, once Google sign-in completes. SPA routes and the
// hash never reach the server, so the server signs everyone in to `/`; the SPA keeps the target in
// sessionStorage, which survives the same-tab OAuth round trip, and follows it from `/`.

import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/** sessionStorage key holding the page to open after sign-in. */
export const POST_LOGIN_KEY = 'postLoginPath';

/**
 * The value when it is a path inside this app, otherwise null. Rejects `//host` and `\` (both can
 * name another origin), every character at or below U+0020 and U+007F (browsers drop tabs and
 * newlines, which turns `/<tab>/evil.example` into `//evil.example`), anything that resolves to
 * another origin or to a `//` path, and the login page itself. Returns the resolved path, query and
 * hash, so dot segments never reach the router.
 */
export function safeInternalPath(value: string | null): string | null {
  if (value === null || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
    return null;
  }
  for (const char of value) {
    const code = char.charCodeAt(0);
    if (code <= 0x20 || code === 0x7f) {
      return null;
    }
  }
  let url: URL;
  try {
    url = new URL(value, window.location.origin);
  } catch {
    return null;
  }
  // Dot segments resolve here, so `/.//evil.example` shows up as a `//` path and is refused.
  const path = url.pathname;
  if (url.origin !== window.location.origin || path.startsWith('//')
      || path.replace(/\/+$/, '').toLowerCase() === '/login') {
    return null;
  }
  return path + url.search + url.hash;
}

/** Keeps a safe target for after sign-in and returns it, or returns null for an unsafe one. */
export function rememberTarget(target: string): string | null {
  const safe = safeInternalPath(target);
  if (safe !== null) {
    try {
      window.sessionStorage.setItem(POST_LOGIN_KEY, safe);
    } catch {
      // Storage blocked: the target still travels in the login URL's `next`.
    }
  }
  return safe;
}

function takeTarget(): string | null {
  try {
    const stored = window.sessionStorage.getItem(POST_LOGIN_KEY);
    window.sessionStorage.removeItem(POST_LOGIN_KEY);
    return stored;
  } catch {
    return null;
  }
}

/**
 * After sign-in lands on `/`, opens the page kept before it. The key is deleted the first time it
 * is read, so the redirect happens once (StrictMode's second effect run finds nothing) and a stale
 * target never fires on a later visit.
 */
export function usePostLoginRedirect(): void {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const target = safeInternalPath(takeTarget());
    if (pathname === '/' && target !== null && target !== '/') {
      navigate(target, { replace: true });
    }
  }, [navigate, pathname]);
}
